// ===== Interfaces =====

export interface KpiData {
  id: string
  label: string
  value: number
  unit: string
  trend: number // % change
  trendDirection: 'up' | 'down'
  sparkline: number[]
}

export interface MonthlyTonnagePoint {
  month: string
  cobre: number
  oro: number
  hierro: number
  zinc: number
  plata: number
}

export interface SupplierRanking {
  id: string
  nombre: string
  ruc: string
  tonelaje: number
  porcentaje: number
  mineralPrincipal: string
}

export interface SupplierMiningLocation {
  supplierId: string
  supplierName: string
  miningRightName: string
  mineral: string
  lat: number
  lng: number
  status: 'Vigente' | 'En Tramite' | 'Suspendido'
}

export interface RouteAnalytics {
  id: string
  origen: string
  destino: string
  distanciaKm: number
  trasladosCompletados: number
  tonelajeTransportado: number
  proveedoresActivos: number
  mineralPrincipal: string
}

export interface MineralBreakdown {
  tipo: string
  tonelaje: number
  porcentaje: number
  color: string
  proveedores: number
}

export interface DestinationFlow {
  destino: string
  cobre: number
  oro: number
  hierro: number
  zinc: number
  plata: number
}

export interface SupplierDetailStats {
  id: string
  nombre: string
  ruc: string
  mineralPrincipal: string
  tonelajeTotal: number
  trasladosTotales: number
  tasaEntrega: number
  mensual: { month: string; tonelaje: number }[]
  minerales: { tipo: string; tonelaje: number; porcentaje: number; color: string }[]
  trasladosRecientes: {
    id: string
    fecha: string
    mineral: string
    tonelaje: number
    destino: string
    estado: 'completado' | 'en_transito' | 'pendiente'
  }[]
}

export interface TransportistaStats {
  id: string
  nombre: string
  ruc: string
  flota: number
  conductores: number
  viajesCompletados: number
  tonelajeTransportado: number
  utilizacionFlota: number
}

export type PeriodFilter = '7d' | '30d' | '90d' | '12m'

export interface DashboardData {
  kpis: KpiData[]
  monthlyTonnage: MonthlyTonnagePoint[]
  topSuppliers: SupplierRanking[]
  mineralBreakdown: MineralBreakdown[]
  routeAnalytics: RouteAnalytics[]
  destinationFlows: DestinationFlow[]
}

// ===== Period Multipliers =====
// Factores para escalar datos segun el periodo seleccionado
const periodFactors: Record<PeriodFilter, { volume: number; trend: number; months: number }> = {
  '7d':  { volume: 0.04, trend: 1.8, months: 2 },   // ~1 semana de un ano
  '30d': { volume: 0.12, trend: 1.3, months: 3 },   // ~1 mes
  '90d': { volume: 0.35, trend: 1.0, months: 5 },   // ~3 meses
  '12m': { volume: 1.0,  trend: 1.0, months: 7 },   // ano completo (base)
}

function scaleKpis(base: KpiData[], factor: typeof periodFactors['12m']): KpiData[] {
  return base.map((kpi) => {
    const isCount = kpi.id === 'rutas' || kpi.id === 'proveedores'
    const scaledValue = isCount
      ? kpi.value // rutas y proveedores no cambian por periodo
      : kpi.id === 'incidencias'
        ? Math.max(1, Math.round(kpi.value * factor.volume))
        : Math.round(kpi.value * factor.volume)
    const sparkSlice = kpi.sparkline.slice(-Math.max(3, Math.round(7 * factor.volume + 1)))
    const scaledSparkline = isCount ? sparkSlice : sparkSlice.map((v) => Math.round(v * factor.volume))
    return {
      ...kpi,
      value: scaledValue,
      trend: +(kpi.trend * factor.trend).toFixed(1),
      sparkline: scaledSparkline,
    }
  })
}

function scaleTonnage(base: MonthlyTonnagePoint[], monthCount: number): MonthlyTonnagePoint[] {
  return base.slice(-monthCount)
}

function scaleSuppliers(base: SupplierRanking[], factor: number): SupplierRanking[] {
  const scaled = base.map((s) => ({
    ...s,
    tonelaje: Math.round(s.tonelaje * factor),
  }))
  const total = scaled.reduce((a, s) => a + s.tonelaje, 0)
  return scaled.map((s) => ({
    ...s,
    porcentaje: +((s.tonelaje / total) * 100).toFixed(1),
  }))
}

function scaleMinerals(base: MineralBreakdown[], factor: number): MineralBreakdown[] {
  const scaled = base.map((m) => ({
    ...m,
    tonelaje: Math.round(m.tonelaje * factor),
  }))
  const total = scaled.reduce((a, m) => a + m.tonelaje, 0)
  return scaled.map((m) => ({
    ...m,
    porcentaje: +((m.tonelaje / total) * 100).toFixed(1),
  }))
}

function scaleRoutes(base: RouteAnalytics[], factor: number): RouteAnalytics[] {
  return base.map((r) => ({
    ...r,
    trasladosCompletados: Math.round(r.trasladosCompletados * factor),
    tonelajeTransportado: Math.round(r.tonelajeTransportado * factor),
  }))
}

function scaleDestinations(base: DestinationFlow[], factor: number): DestinationFlow[] {
  return base.map((d) => ({
    ...d,
    cobre: Math.round(d.cobre * factor),
    oro: Math.round(d.oro * factor),
    hierro: Math.round(d.hierro * factor),
    zinc: Math.round(d.zinc * factor),
    plata: Math.round(d.plata * factor),
  }))
}

/** Retorna todos los datos del dashboard escalados segun el periodo */
export function getDataByPeriod(period: PeriodFilter): DashboardData {
  const factor = periodFactors[period]
  return {
    kpis: scaleKpis(kpiData, factor),
    monthlyTonnage: scaleTonnage(monthlyTonnage, factor.months),
    topSuppliers: scaleSuppliers(topSuppliers, factor.volume),
    mineralBreakdown: scaleMinerals(mineralBreakdown, factor.volume),
    routeAnalytics: scaleRoutes(routeAnalytics, factor.volume),
    destinationFlows: scaleDestinations(destinationFlows, factor.volume),
  }
}

// ===== Mock Data (base 12m) =====

export const kpiData: KpiData[] = [
  {
    id: 'tonelaje',
    label: 'Tonelaje Total',
    value: 187450,
    unit: 'TM',
    trend: 12.5,
    trendDirection: 'up',
    sparkline: [145000, 152000, 160000, 155000, 172000, 180000, 187450],
  },
  {
    id: 'traslados',
    label: 'Traslados Activos',
    value: 142,
    unit: '',
    trend: 8.3,
    trendDirection: 'up',
    sparkline: [98, 105, 112, 120, 128, 135, 142],
  },
  {
    id: 'rutas',
    label: 'Rutas Activas',
    value: 5,
    unit: '',
    trend: 25.0,
    trendDirection: 'up',
    sparkline: [2, 3, 3, 4, 4, 5, 5],
  },
  {
    id: 'tonelajeMensual',
    label: 'Tonelaje Mensual',
    value: 40600,
    unit: 'TM',
    trend: 9.8,
    trendDirection: 'up',
    sparkline: [30500, 33100, 34900, 35100, 36900, 38800, 40600],
  },
  {
    id: 'proveedores',
    label: 'Proveedores Activos',
    value: 38,
    unit: '',
    trend: 5.6,
    trendDirection: 'up',
    sparkline: [28, 30, 32, 33, 35, 36, 38],
  },
  {
    id: 'incidencias',
    label: 'Incidencias',
    value: 7,
    unit: '',
    trend: 22.2,
    trendDirection: 'down',
    sparkline: [15, 12, 11, 10, 9, 8, 7],
  },
]

export const monthlyTonnage: MonthlyTonnagePoint[] = [
  { month: 'Jul', cobre: 12000, oro: 3500, hierro: 8000, zinc: 5000, plata: 2000 },
  { month: 'Ago', cobre: 13500, oro: 3800, hierro: 8500, zinc: 5200, plata: 2100 },
  { month: 'Sep', cobre: 14200, oro: 4000, hierro: 9000, zinc: 5500, plata: 2200 },
  { month: 'Oct', cobre: 13800, oro: 3600, hierro: 8800, zinc: 5300, plata: 2000 },
  { month: 'Nov', cobre: 15000, oro: 4200, hierro: 9500, zinc: 5800, plata: 2400 },
  { month: 'Dic', cobre: 15800, oro: 4500, hierro: 10000, zinc: 6000, plata: 2500 },
  { month: 'Ene', cobre: 16500, oro: 4800, hierro: 10500, zinc: 6200, plata: 2600 },
]

export const supplierMiningLocations: SupplierMiningLocation[] = [
  { supplierId: '1', supplierName: 'Minera Cerro Verde', miningRightName: 'Cerro Verde I', mineral: 'Cobre', lat: -16.536, lng: -71.601, status: 'Vigente' },
  { supplierId: '1', supplierName: 'Minera Cerro Verde', miningRightName: 'Cerro Verde II', mineral: 'Cobre', lat: -16.548, lng: -71.612, status: 'Vigente' },
  { supplierId: '2', supplierName: 'Southern Peru', miningRightName: 'Toquepala', mineral: 'Cobre', lat: -17.249, lng: -70.602, status: 'Vigente' },
  { supplierId: '2', supplierName: 'Southern Peru', miningRightName: 'Cuajone', mineral: 'Cobre', lat: -17.044, lng: -70.709, status: 'Vigente' },
  { supplierId: '3', supplierName: 'Antamina', miningRightName: 'Antamina Principal', mineral: 'Zinc', lat: -9.531, lng: -77.058, status: 'Vigente' },
  { supplierId: '3', supplierName: 'Antamina', miningRightName: 'Antamina Norte', mineral: 'Zinc', lat: -9.512, lng: -77.041, status: 'En Tramite' },
  { supplierId: '4', supplierName: 'Yanacocha', miningRightName: 'Yanacocha Norte', mineral: 'Oro', lat: -6.973, lng: -78.534, status: 'Vigente' },
  { supplierId: '4', supplierName: 'Yanacocha', miningRightName: 'Yanacocha Sur', mineral: 'Oro', lat: -7.012, lng: -78.551, status: 'Vigente' },
  { supplierId: '5', supplierName: 'Volcan Compania', miningRightName: 'Carahuacra', mineral: 'Zinc', lat: -11.641, lng: -76.059, status: 'Vigente' },
  { supplierId: '5', supplierName: 'Volcan Compania', miningRightName: 'San Cristobal', mineral: 'Zinc', lat: -11.585, lng: -76.098, status: 'Suspendido' },
  { supplierId: '6', supplierName: 'Minsur', miningRightName: 'San Rafael', mineral: 'Hierro', lat: -14.717, lng: -70.317, status: 'Vigente' },
  { supplierId: '6', supplierName: 'Minsur', miningRightName: 'Pucamarca', mineral: 'Hierro', lat: -17.527, lng: -69.948, status: 'Vigente' },
  { supplierId: '7', supplierName: 'Compania Buenaventura', miningRightName: 'Orcopampa', mineral: 'Oro', lat: -15.262, lng: -72.337, status: 'Vigente' },
  { supplierId: '7', supplierName: 'Compania Buenaventura', miningRightName: 'Uchucchacua', mineral: 'Oro', lat: -10.627, lng: -76.685, status: 'En Tramite' },
  { supplierId: '8', supplierName: 'Minera Barrick', miningRightName: 'Lagunas Norte', mineral: 'Oro', lat: -7.946, lng: -78.223, status: 'Vigente' },
  { supplierId: '8', supplierName: 'Minera Barrick', miningRightName: 'Pierina', mineral: 'Oro', lat: -9.442, lng: -77.588, status: 'Suspendido' },
]

export const topSuppliers: SupplierRanking[] = [
  { id: '1', nombre: 'Minera Cerro Verde', ruc: '20100154308', tonelaje: 35200, porcentaje: 18.8, mineralPrincipal: 'Cobre' },
  { id: '2', nombre: 'Southern Peru', ruc: '20100147514', tonelaje: 28500, porcentaje: 15.2, mineralPrincipal: 'Cobre' },
  { id: '3', nombre: 'Antamina', ruc: '20463222612', tonelaje: 25800, porcentaje: 13.8, mineralPrincipal: 'Zinc' },
  { id: '4', nombre: 'Yanacocha', ruc: '20137367558', tonelaje: 22100, porcentaje: 11.8, mineralPrincipal: 'Oro' },
  { id: '5', nombre: 'Volcan Compania', ruc: '20383045734', tonelaje: 19500, porcentaje: 10.4, mineralPrincipal: 'Zinc' },
  { id: '6', nombre: 'Minsur', ruc: '20100136741', tonelaje: 18200, porcentaje: 9.7, mineralPrincipal: 'Hierro' },
  { id: '7', nombre: 'Compania Buenaventura', ruc: '20100116635', tonelaje: 15800, porcentaje: 8.4, mineralPrincipal: 'Oro' },
  { id: '8', nombre: 'Minera Barrick', ruc: '20507862168', tonelaje: 12300, porcentaje: 6.6, mineralPrincipal: 'Oro' },
]

export const routeAnalytics: RouteAnalytics[] = [
  { id: '1', origen: 'Cerro de Pasco', destino: 'Puerto Callao', distanciaKm: 305, trasladosCompletados: 342, tonelajeTransportado: 45600, proveedoresActivos: 8, mineralPrincipal: 'Cobre' },
  { id: '2', origen: 'Cajamarca', destino: 'Puerto Salaverry', distanciaKm: 185, trasladosCompletados: 278, tonelajeTransportado: 32100, proveedoresActivos: 5, mineralPrincipal: 'Oro' },
  { id: '3', origen: 'Arequipa', destino: 'Puerto Matarani', distanciaKm: 120, trasladosCompletados: 456, tonelajeTransportado: 58200, proveedoresActivos: 10, mineralPrincipal: 'Cobre' },
  { id: '4', origen: 'Huancavelica', destino: 'Lima (Planta)', distanciaKm: 440, trasladosCompletados: 198, tonelajeTransportado: 21500, proveedoresActivos: 4, mineralPrincipal: 'Zinc' },
  { id: '5', origen: 'Cusco', destino: 'Puerto Matarani', distanciaKm: 510, trasladosCompletados: 156, tonelajeTransportado: 18700, proveedoresActivos: 3, mineralPrincipal: 'Hierro' },
]

export const mineralBreakdown: MineralBreakdown[] = [
  { tipo: 'Cobre', tonelaje: 72500, porcentaje: 38.7, color: '#d49a2a', proveedores: 12 },
  { tipo: 'Oro', tonelaje: 28500, porcentaje: 15.2, color: '#FBBF24', proveedores: 8 },
  { tipo: 'Hierro', tonelaje: 35200, porcentaje: 18.8, color: '#EA580C', proveedores: 6 },
  { tipo: 'Zinc', tonelaje: 32000, porcentaje: 17.1, color: '#3d6fa8', proveedores: 7 },
  { tipo: 'Plata', tonelaje: 12800, porcentaje: 6.8, color: '#b5aa9d', proveedores: 5 },
  { tipo: 'Otros', tonelaje: 6450, porcentaje: 3.4, color: '#857b71', proveedores: 3 },
]

export const destinationFlows: DestinationFlow[] = [
  { destino: 'Puerto Callao', cobre: 28000, oro: 12000, hierro: 8000, zinc: 15000, plata: 5000 },
  { destino: 'Puerto Matarani', cobre: 22000, oro: 8000, hierro: 18000, zinc: 10000, plata: 4000 },
  { destino: 'Puerto Salaverry', cobre: 15000, oro: 6000, hierro: 6000, zinc: 5000, plata: 2500 },
  { destino: 'Lima (Planta)', cobre: 7500, oro: 2500, hierro: 3200, zinc: 2000, plata: 1300 },
]

export const supplierDetails: SupplierDetailStats[] = topSuppliers.map((s) => ({
  id: s.id,
  nombre: s.nombre,
  ruc: s.ruc,
  mineralPrincipal: s.mineralPrincipal,
  tonelajeTotal: s.tonelaje,
  trasladosTotales: Math.round(s.tonelaje / 25),
  tasaEntrega: 90 + Math.random() * 9,
  mensual: [
    { month: 'Jul', tonelaje: Math.round(s.tonelaje * 0.12) },
    { month: 'Ago', tonelaje: Math.round(s.tonelaje * 0.13) },
    { month: 'Sep', tonelaje: Math.round(s.tonelaje * 0.14) },
    { month: 'Oct', tonelaje: Math.round(s.tonelaje * 0.13) },
    { month: 'Nov', tonelaje: Math.round(s.tonelaje * 0.15) },
    { month: 'Dic', tonelaje: Math.round(s.tonelaje * 0.16) },
    { month: 'Ene', tonelaje: Math.round(s.tonelaje * 0.17) },
  ],
  minerales: [
    { tipo: s.mineralPrincipal, tonelaje: Math.round(s.tonelaje * 0.65), porcentaje: 65, color: '#d49a2a' },
    { tipo: 'Otros', tonelaje: Math.round(s.tonelaje * 0.35), porcentaje: 35, color: '#857b71' },
  ],
  trasladosRecientes: Array.from({ length: 5 }, (_, i) => ({
    id: `TR-${s.id}${String(i + 1).padStart(3, '0')}`,
    fecha: `2026-01-${String(25 - i).padStart(2, '0')}`,
    mineral: s.mineralPrincipal,
    tonelaje: 20 + Math.round(Math.random() * 30),
    destino: ['Puerto Callao', 'Puerto Matarani', 'Puerto Salaverry', 'Lima (Planta)'][i % 4],
    estado: (['completado', 'completado', 'en_transito', 'completado', 'pendiente'] as const)[i],
  })),
}))

export const transportistaStats: TransportistaStats[] = [
  { id: '1', nombre: 'Transporte San Martin SAC', ruc: '20512345678', flota: 45, conductores: 52, viajesCompletados: 1280, tonelajeTransportado: 38400, utilizacionFlota: 88 },
  { id: '2', nombre: 'Logistica Andina EIRL', ruc: '20523456789', flota: 32, conductores: 38, viajesCompletados: 945, tonelajeTransportado: 28350, utilizacionFlota: 82 },
  { id: '3', nombre: 'Transportes del Sur SA', ruc: '20534567890', flota: 28, conductores: 30, viajesCompletados: 820, tonelajeTransportado: 24600, utilizacionFlota: 79 },
  { id: '4', nombre: 'Carga Pesada Peru SAC', ruc: '20545678901', flota: 22, conductores: 26, viajesCompletados: 680, tonelajeTransportado: 20400, utilizacionFlota: 85 },
  { id: '5', nombre: 'Express Minero SA', ruc: '20556789012', flota: 18, conductores: 20, viajesCompletados: 540, tonelajeTransportado: 16200, utilizacionFlota: 76 },
  { id: '6', nombre: 'Volquetes Nacional SAC', ruc: '20567890123', flota: 35, conductores: 40, viajesCompletados: 1050, tonelajeTransportado: 31500, utilizacionFlota: 72 },
  { id: '7', nombre: 'Ruta Segura Logistica', ruc: '20578901234', flota: 15, conductores: 18, viajesCompletados: 420, tonelajeTransportado: 12600, utilizacionFlota: 91 },
  { id: '8', nombre: 'Minera Transport EIRL', ruc: '20589012345', flota: 20, conductores: 22, viajesCompletados: 590, tonelajeTransportado: 17700, utilizacionFlota: 74 },
]
